import { UseMutationOptions, useMutation } from "@tanstack/react-query";
import { useState } from "react";

export type DownloadProgress = number | null;

export type DownloadHelpers = {
    onProgress: (progress: DownloadProgress) => void;
};

export type DownloadFn<TVariables, TData> = (variables: TVariables, helpers: DownloadHelpers) => Promise<TData>;

type UseDownloadOptions<TData, TError, TVariables> = Omit<UseMutationOptions<TData, TError, TVariables>, "mutationFn">;

/**
 * react-query mutation wrapper for a file download. The caller supplies
 * `downloadFn` (fetch a blob, stream to disk, ...) and reports progress
 * through it via `onProgress` — `null` means indeterminate.
 */
export function useDownload<TVariables, TData = void, TError = Error>(
    downloadFn: DownloadFn<TVariables, TData>,
    options: UseDownloadOptions<TData, TError, TVariables> = {},
) {
    const [progress, setProgress] = useState<DownloadProgress>(null);

    const mutation = useMutation<TData, TError, TVariables>({
        mutationFn: variables => {
            setProgress(null);

            return downloadFn(variables, { onProgress: setProgress });
        },
        ...options,
    });

    return {
        download: mutation.mutate,
        downloadAsync: mutation.mutateAsync,
        isPending: mutation.isPending,
        isError: mutation.isError,
        isSuccess: mutation.isSuccess,
        error: mutation.error,
        reset: () => {
            mutation.reset();
            setProgress(null);
        },
        progress,
    };
}
